import { useEffect, useState } from 'react';
import styles from './AddPhoneNumber.module.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { getTenantDetails, getUserByToken, updateTenantPhone } from '../../auth/_requests';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatPhoneNumber } from '../../helpers/utils/utils';

import { isPhoneNumber } from 'class-validator';

import { useAuth } from '../../auth/Auth';
import { isValidMobilePhone } from '../../helpers/utils/phoneNumber';
import { ChevronLeftIcon, InfoIcon } from 'lucide-react';
import { Popover, Whisper } from 'rsuite';
import loginStyles from '../Login/Login.module.css';
import { AuthPageShell } from '../Login/AuthPageShell';
import { trackOnboardingStart, trackOnboardingStepCompleted } from '../../helpers/utils/analytics';

const registrationSchema = Yup.object().shape({
  phone: Yup.string()
    .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
    .required('Phone number is required')
    .test('isValidPhone', 'Please provide a valid Canadian or US phone number', (value) =>
      isValidMobilePhone(value)
    ),
  acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
  acceptSMS: Yup.bool().required('You must accept the SMS terms and conditions'),
});

const AddPhoneNumber = () => {
  const [loading, setLoading] = useState(false);
  const { auth, currentUser, setCurrentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [isInValid, setInValid] = useState(false);

  useEffect(() => {
    trackOnboardingStart();
  }, []);

  const initialValues = {
    phone: currentUser?.user?.phoneNumber || '',
    phoneExtension: '1',
    acceptTerms: false,
    acceptSMS: false,
  };

  const handleSkipForNow = async () => {
    try {
      const response = await getTenantDetails(auth?.data.token);
      if (response?.data?.tenantBuildings.length > 0) {
        navigate('/dashboard');
      } else {
        navigate('/tenant-type');
      }
    } catch (error) {
      console.error('Error fetching tenant details:', error);
      navigate('/tenant-type');
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        await updateTenantPhone(auth?.data.token, {
          userDetails: { phoneNumber: values.phone },
        });
        if (auth?.data.token) {
          const { data: updatedUser } = await getUserByToken(auth?.data.token);
          setCurrentUser(updatedUser);
        }

        // Update context
        trackOnboardingStepCompleted('add_phone_number');

        navigate('/verify-phone');
      } catch (error: unknown) {
        console.error(error);
        const isBlockedPhone =
          error instanceof Error && error.message === 'phone_number_blocked';
        setStatus(
          isBlockedPhone
            ? 'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
            : 'An error occurred while updating the phone number. Please try again later.'
        );
        setSubmitting(false);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <AuthPageShell variant='center'>
      <div className={loginStyles.authFlowMax}>
        <motion.main className={`${loginStyles.main} ${loginStyles.mainWide}`} initial={false}>
          <button
            type='button'
            className='flex items-center gap-3 bg-transparent border-none cursor-pointer p-0 w-full text-left'
            onClick={() => {
              logout();
              navigate('/sign-up');
            }}
          >
            <ChevronLeftIcon className='text-white/70' stroke='currentColor' />
            <p className='font-display text-lg font-bold tracking-tight text-white'>
              Add your phone number
            </p>
          </button>
          <div className={styles.headingBox}>
            <div className='max-w-[360px] mx-auto flex flex-col gap-5 mt-2'>
              <p className='text-base text-[var(--dbody)] font-semibold text-center leading-6'>
                Secure your TenantPay account
              </p>

              <p className='text-sm text-[var(--dbody)] font-normal text-center leading-relaxed'>
                Your phone number is required to enable{' '}
                <b className='text-white'>Multi-Factor Authentication (MFA)</b>.
                <Whisper
                  trigger='hover'
                  placement='right'
                  speaker={
                    <Popover className='max-w-[300px] text-[#0E0E0B] leading-[20px] p-3'>
                      MFA helps protect your account by verifying your identity before access is
                      granted. A secure code will be sent to your phone during sign-in or sensitive
                      actions, ensuring only you can authorize access.
                    </Popover>
                  }
                >
                  <span className='inline-flex items-center justify-center cursor-pointer ml-1'>
                    <InfoIcon size={16} className='text-white/50' stroke='currentColor' />
                  </span>
                </Whisper>
              </p>
            </div>
          </div>
          {formik.status && (
            <div className={styles.statusBox}>
              <div className={styles.status}>{formik.status}</div>
            </div>
          )}
          <div>
            <form
              className={styles.form}
              noValidate
              id='kt_login_signup_form'
              onSubmit={formik.handleSubmit}
            >
              <div className={styles.nameBox}>
                <div className={styles.lastName}>
                  <div className={styles.tooltipWrap}>
                    <label className={styles.label}>
                      Phone number<span className={styles.mandatory}>*</span>
                    </label>
                  </div>
                  <div
                    className={clsx(
                      styles.phoneInputBox,
                      formik.touched.phone && (formik.errors.phone || isInValid)
                        ? styles.isInvalid
                        : '',
                      formik.touched.phone && !formik.errors.phone && !isInValid ? styles.valid : ''
                    )}
                  >
                    <div className={styles.dropdown}>+1</div>
                    <input
                      type='text'
                      autoComplete='off'
                      {...formik.getFieldProps('phone')}
                      className={styles.phoneInput}
                      placeholder='(639) 212-4561'
                      maxLength={14}
                      value={formatPhoneNumber(formik.values.phone)}
                      onChange={(e) => {
                        const formattedPhone = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
                        formik.setFieldValue('phone', formattedPhone);
                        if (formattedPhone.length === 10) {
                          if (!isPhoneNumber(formattedPhone, 'CA')) {
                            setInValid(true);
                          } else {
                            setInValid(false);
                            formik.setFieldError('phone', '');
                          }
                        } else {
                          setInValid(false);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          formik.handleSubmit();
                        }
                      }}
                    />
                  </div>
                  {formik.touched.phone && formik.errors.phone && (
                    <div className={styles.error}>
                      <span>{formik.errors.phone}</span>
                    </div>
                  )}
                  {formik.touched.phone && isInValid && (
                    <div className={styles.error}>
                      <span>{'Please provide a valid Canadian or US phone number'}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className='text-center pt-2'>
                <button
                  type='submit'
                  disabled={isInValid}
                  className={`${loginStyles.submitBtn} ${
                    (formik.isSubmitting || !formik.isValid || isInValid) && loginStyles.disableBtn
                  }`}
                >
                  {!loading && <span>Next</span>}
                  {loading && (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Please wait... <span className={styles.spinner}></span>
                    </span>
                  )}
                </button>
                <button
                  type='button'
                  className={styles.skipBtn}
                  onClick={handleSkipForNow}
                  disabled={loading}
                >
                  Skip for now
                </button>
              </div>
            </form>
          </div>
        </motion.main>
      </div>
    </AuthPageShell>
  );
};

export default AddPhoneNumber;
